import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import './styles/Landlord.css';
import { useLandlordGuard } from '../../utils/authGuard';
import { GoogleMap, useLoadScript, Autocomplete } from '@react-google-maps/api';
import ApartmentHouseForm from './PropertyForms/ApartmentHouseForm';
import LodgeForm from './PropertyForms/LodgeForm';
import RoomForm from './PropertyForms/RoomForm';
import BoardingForm from './PropertyForms/BoardingForm';

const AddProperty = () => {
  const { isAuthorized } = useLandlordGuard();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [autoCompleteError, setAutoCompleteError] = useState('');
  const base_url = 'https://api.homehive.co.zw/api';
  const [currentStep, setCurrentStep] = useState(1); // 1: Type, 2: Details, 3: Media

  const propertyTypes = [
    { value: 'apartment', label: 'Apartment', icon: 'building' },
    { value: 'full_house', label: 'House', icon: 'home' },
    { value: 'room', label: 'Room', icon: 'door-open' },
    { value: 'lodge', label: 'Lodge', icon: 'door-open' },
    { value: 'boarding_house', label: 'Boarding House', icon: 'warehouse' }
  ];

  const [formData, setFormData] = useState({
    property_type: '',
    description: '',
    monthly_rental: '',
    location: '',
    latitude: '',
    longitude: '',
    availability_status: '',
    property_tour: null,
    has_power_backup: false,
    power_backup: '',
    water_source: '',
    is_deposit_required: false,
    deposit_amount: '',
    is_gated: false,
    nearby_hospital: '',
    nearby_school: '',
    type_of_bathroom: '',
    has_parking_space: false,
    parking_slots: '',
    bedrooms: '',
    is_furnished: false,
    has_pool: false,
    has_gym: false,
    has_cctv: false,
    has_security: false,
    pets_allowed: false,
    has_electricity: false,
    // Lodge specific fields
    price_per_night: '',
    total_rooms: '',
    has_restaurant: false,
    has_bar: false,
    has_outdoor_space: false,
    has_wifi: false,
    has_tv: false,
    has_air_conditioning: false,
    max_occupants: '',
    has_study_area: false,
    has_common_area: false,
    has_kitchen: false,
    gender_restriction: '',
  });

  const [videoPreview, setVideoPreview] = useState(null);

  // Memoize the libraries array to prevent LoadScript reload
  const libraries = useMemo(() => ['places'], []);

  const [autocomplete, setAutocomplete] = useState(null);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY',
    libraries: libraries
  });
  const autocompleteRef = useRef(null);

  const onLoad = (autoC) => {
    setAutocomplete(autoC);
    autocompleteRef.current = autoC;
  };

  const onPlaceChanged = () => {
    try {
      const autoC = autocomplete || autocompleteRef.current;
      if (!autoC) {
        setAutoCompleteError('Autocomplete not initialized.');
        return;
      }
      const place = autoC.getPlace();
      if (!place || !place.geometry) {
        setAutoCompleteError('No place details available.');
        return;
      }
      const address = place.formatted_address || '';
      const lat = place.geometry.location?.lat();
      const lng = place.geometry.location?.lng();
      if (!lat || !lng) {
        setAutoCompleteError('No coordinates found for this place.');
        return;
      }
      setFormData(prev => ({
        ...prev,
        location: address,
        latitude: lat,
        longitude: lng
      }));
      setAutoCompleteError('');
    } catch (err) {
      setAutoCompleteError('Error getting place details.');
      console.error('Autocomplete error:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'city') {
      // When city is selected, update both city and city_id
      setFormData(prev => ({
        ...prev,
        city_id: value // The value from the select is already the city ID
      }));
    } else {
      // Handle all other inputs normally
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value
      }));
    }
  };

  const handleRemoveImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviewImages(prev => {
      // Revoke the URL to prevent memory leaks
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleImageChange = (e) => {
    const scrollPosition = window.scrollY;
    const files = Array.from(e.target.files);

    if (files.length > 5) {
      Swal.fire({
        title: 'Error',
        text: 'Maximum 5 images allowed',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
      return;
    }

    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire({
          title: 'Error',
          text: `File ${file.name} is too large. Maximum size is 5MB`,
          icon: 'error',
          confirmButtonColor: '#1a237e'
        });
        return false;
      }
      return true;
    });

    // Store the actual files
    setImages(prevImages => [...prevImages, ...validFiles]);

    // Create preview URLs
    const newPreviews = validFiles.map(file => URL.createObjectURL(file));
    setPreviewImages(prevPreviews => [...prevPreviews, ...newPreviews]);

    window.scrollTo(0, scrollPosition);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    const files = Array.from(e.dataTransfer.files);
    handleImageChange({ target: { files } });
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      // Check file type
      if (!file.type.startsWith('video/')) {
        Swal.fire({
          title: 'Error',
          text: 'Please upload a valid video file',
          icon: 'error',
          confirmButtonColor: '#1a237e'
        });
        return;
      }

      // Check file size (limit to 100MB)
      if (file.size > 100 * 1024 * 1024) {
        Swal.fire({
          title: 'Error',
          text: 'Video file size should not exceed 100MB',
          icon: 'error',
          confirmButtonColor: '#1a237e'
        });
        return;
      }

      setFormData(prev => ({
        ...prev,
        property_tour: file
      }));

      // Create video preview URL
      const videoURL = URL.createObjectURL(file);
      setVideoPreview(videoURL);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.property_type) {
      errors.property_type = 'Property type is required';
    }
    if (!formData.description) {
      errors.description = 'Description is required';
    }
    if (!formData.location) {
      errors.location = 'Location is required';
    }

    // Property type specific validation
    switch (formData.property_type) {
      case 'apartment':
      case 'full_house':
        if (!formData.monthly_rental) {
          errors.monthly_rental = 'Monthly rental is required';
        }
        if (!formData.bedrooms) {
          errors.bedrooms = `Bedrooms are required for ${formData.property_type}s`;
        }

        if (!formData.water_source) {
          errors.water_source = 'Water source is required';
        }
        if (formData.has_parking_space && !formData.parking_slots) {
          errors.parking_slots = 'Number of parking slots is required when parking space is available';
        }
        if (formData.has_power_backup && !formData.power_backup) {
          errors.power_backup = 'Power backup details are required when power backup is available';
        }
        if (formData.is_deposit_required && !formData.deposit_amount) {
          errors.deposit_amount = 'Deposit amount is required when deposit is required';
        }
        break;

      case 'room':
        if (!formData.monthly_rental) {
          errors.monthly_rental = 'Monthly rental is required for rooms';
        }
        if (!formData.water_source) {
          errors.water_source = 'Water source is required';
        }
        if (!formData.type_of_bathroom) {
          errors.type_of_bathroom = 'Bathroom type is required for rooms';
        }
        if (formData.has_power_backup && !formData.power_backup) {
          errors.power_backup = 'Power backup details are required when power backup is available';
        }
        if (formData.is_deposit_required && !formData.deposit_amount) {
          errors.deposit_amount = 'Deposit amount is required when deposit is required';
        }
        break;

      case 'lodge':
        if (!formData.price_per_night) {
          errors.price_per_night = 'Price per night is required';
        }
        if (!formData.type_of_bathroom) {
          errors.type_of_bathroom = 'Bathroom type is required for lodges';
        }
        break;

      case 'boarding_house':
        if (!formData.max_occupants) {
          errors.max_occupants = 'Maximum occupants is required';
        }
        if (!formData.gender_restriction) {
          errors.gender_restriction = 'Gender restriction is required';
        }
        break;

      default:
        break;
    }

    return errors;
  };

  const validateStep = (step) => {
    const stepErrors = {};
    console.log('validateStep called for step:', step, 'property_type:', formData.property_type);
    
    if (step === 1) {
      if (!formData.property_type) {
        stepErrors.property_type = 'Please select a property type to continue';
      }
    }
    if (step === 2) {
      // Reuse existing validation for details (no media required here)
      const v = validateForm();
      Object.assign(stepErrors, v);
    }
    
    console.log('Step errors:', stepErrors);
    setErrors(stepErrors);
    const isValid = Object.keys(stepErrors).length === 0;
    console.log('Step validation result:', isValid);
    return isValid;
  };

  const goToNextStep = () => {
    console.log('goToNextStep called, currentStep:', currentStep, 'property_type:', formData.property_type);
    if (validateStep(currentStep)) {
      console.log('Validation passed, moving to next step');
      setCurrentStep(prev => Math.min(3, prev + 1));
    } else {
      console.log('Validation failed, errors:', errors);
    }
  };

  const goToPrevStep = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate form
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setIsLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();

      // Add all form fields to FormData
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '' && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add images to FormData
      images.forEach((image, index) => {
        formDataToSend.append('images', image);
      });

      // Add video tour if exists
      if (formData.property_tour) {
        formDataToSend.append('property_tour', formData.property_tour);
      }

      // Ensure latitude and longitude are sent
      if (formData.latitude) formDataToSend.append('latitude', formData.latitude);
      if (formData.longitude) formDataToSend.append('longitude', formData.longitude);
      console.log(formDataToSend);

      const response = await axios.post(
        `${base_url}/properties/`,
        formDataToSend,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      console.log(response.data);

      Swal.fire({
        title: 'Success!',
        text: 'Property has been added successfully',
        icon: 'success',
        confirmButtonColor: '#1a237e'
      }).then(() => {
        navigate('/landlord/properties');
      });
    } catch (error) {
      console.error('Error adding property:', error);
      const errorMessage = error.response?.data?.detail ||
        Object.values(error.response?.data || {})[0] ||
        'Failed to add property';

      Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderPropertyTypeFields = () => {
    switch (formData.property_type) {
      case 'apartment':
      case 'full_house':
        return <ApartmentHouseForm formData={formData} handleInputChange={handleInputChange} errors={errors} />;
      case 'room':
        return <RoomForm formData={formData} handleInputChange={handleInputChange} errors={errors} />;
      case 'lodge':
        return <LodgeForm formData={formData} handleInputChange={handleInputChange} errors={errors} />;
      case 'boarding_house':
        return <BoardingForm formData={formData} handleInputChange={handleInputChange} errors={errors} />;
      default:
        return null;
    }
  };

  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      Swal.fire({
        title: 'Error',
        text: 'Geolocation is not supported by your browser.',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
      return;
    }

    // Check if we're on HTTPS (required for geolocation on mobile)
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      Swal.fire({
        title: 'HTTPS Required',
        text: 'Location access requires a secure connection (HTTPS). Please use HTTPS or localhost.',
        icon: 'warning',
        confirmButtonColor: '#1a237e'
      });
      return;
    }

    setIsLoading(true);
    
    // Mobile-optimized geolocation options
    const options = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 seconds timeout
      maximumAge: 300000 // 5 minutes cache
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        console.log('Geolocation success:', lat, lng);
        
        try {
          const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
          if (!apiKey || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY') {
            throw new Error('Google Maps API key is missing or invalid.');
          }
          
          const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          console.log('Geocoding response:', data);
          
          if (data.status !== 'OK') {
            throw new Error(`Geocoding failed: ${data.status}`);
          }
          
          const address = data.results && data.results[0] ? data.results[0].formatted_address : '';
          
          if (!address) {
            throw new Error('No address found for these coordinates');
          }
          
          setFormData(prev => ({
            ...prev,
            location: address,
            latitude: lat,
            longitude: lng
          }));
          setAutoCompleteError('');
          
          Swal.fire({
            title: 'Success!',
            text: 'Your location has been detected and added.',
            icon: 'success',
            confirmButtonColor: '#1a237e',
            timer: 2000
          });
          
        } catch (error) {
          console.error('Geocoding error:', error);
          setAutoCompleteError('Failed to retrieve address from coordinates.');
          Swal.fire({
            title: 'Error',
            text: error.message || 'Failed to retrieve address from coordinates.',
            icon: 'error',
            confirmButtonColor: '#1a237e'
          });
        }
        setIsLoading(false);
      }, 
      (error) => {
        console.error('Geolocation error:', error);
        setIsLoading(false);
        
        let errorMessage = 'Unable to retrieve your location.';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions in your browser settings and try again.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable. Please check your GPS/WiFi settings.';
            break;
          default:
            errorMessage = 'An unknown error occurred while retrieving location.';
            break;
        }
        
        setAutoCompleteError(errorMessage);
        Swal.fire({
          title: 'Location Error',
          text: errorMessage,
          icon: 'error',
          confirmButtonColor: '#1a237e'
        });
      },
      options
    );
  };

  useEffect(() => {
    return () => {
      if (videoPreview) {
        URL.revokeObjectURL(videoPreview);
      }
    };
  }, [videoPreview]);

  if (!isAuthorized) return null;

  return (
    <div className="add-property-wrapper">
      <div className="add-property-container">
        <div className="property-form-card">
          <div className="add-property-header">

            <div style={{padding: '30px 0 0 30px'}} className="header-content">
              <h1>
                <i className="fas fa-home fa-bounce"></i>
                Add Your Property
              </h1>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="add-property-form">
            {/* Step 1: Property Type */}
            {currentStep === 1 && (
              <div className="form-section">
                <div className="section-header">
                  <h2><i className="fas fa-home"></i> Choose Property Type</h2>
                </div>
                <div
                  className="property-type-grid"
                >
                  {propertyTypes.map(type => {
                    const isSelected = formData.property_type === type.value;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, property_type: type.value }))}
                        className={`property-type-card${isSelected ? ' selected' : ''}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '14px 16px',
                          borderRadius: '8px',
                          border: isSelected ? '2px solid #1a237e' : '1px solid #e0e0e0',
                          background: isSelected ? '#e8eaf6' : '#ffffff',
                          color: isSelected ? '#1a237e' : '#333',
                          cursor: 'pointer',
                          boxShadow: isSelected ? '0 2px 8px rgba(26,35,126,0.15)' : '0 1px 4px rgba(0,0,0,0.06)'
                        }}
                        aria-pressed={isSelected}
                      >
                        <span style={{ fontWeight: 600 }}>{type.label}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.property_type && (
                  <div className="error-text" style={{ marginTop: '10px' }}>{errors.property_type}</div>
                )}
              </div>
            )}

            {/* Step 2: Details (except media) */}
            {currentStep === 2 && formData.property_type && (
              <div className="form-section">
                <div className="section-header">
                  <h2><i className="fas fa-info-circle"></i> Property Details</h2>
                </div>
                <div className="form-grid">
                  {/* Description */}
                  <div className="form-group full-width">
                    <label>
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      placeholder="Write a compelling description of your property..."
                      rows="3"
                      className="fancy-input description-input"
                      inputMode="text"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    />
                  </div>

                  {/* Price and Location */}
                  <div className="form-group">
                    <label>
                      {formData.property_type === 'lodge' ? 'Price Per Night (USD)' : 'Monthly Rent (USD)'}
                    </label>
                    <div className="price-input">
                      <input
                        type="number"
                        name={formData.property_type === 'lodge' ? 'price_per_night' : 'monthly_rental'}
                        value={formData.property_type === 'lodge' ? formData.price_per_night : formData.monthly_rental}
                        onChange={handleInputChange}
                        required
                        min="0"
                        step="0.01"
                        placeholder={formData.property_type === 'lodge' ? 'Enter price per night' : 'Enter monthly rent'}
                        className="fancy-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      Location
                    </label>

                    {isLoaded ? (
                      <Autocomplete
                        onLoad={onLoad}
                        onPlaceChanged={onPlaceChanged}
                        ref={autocompleteRef}
                      >
                        <input
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          required
                          placeholder="Search for address or place"
                          className="fancy-input"
                          style={{ flex: 1 }}
                        />
                      </Autocomplete>
                    ) : (
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter street address"
                        className="fancy-input"
                        style={{ flex: 1 }}
                      />
                    )}
                    <button
                      type="button"
                      className="current-location-btn"
                      onClick={handleUseCurrentLocation}
                      disabled={isLoading}
                      title="Use my current location"
                      style={{ whiteSpace: 'nowrap', padding: '8px 12px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-location-arrow"></i>}
                      &nbsp;My Location
                    </button>
                  </div>
                  {autoCompleteError && (
                    <div style={{ color: 'red', marginTop: '4px' }}>{autoCompleteError}</div>
                  )}

                  {/* Property Type Specific Fields */}
                  {renderPropertyTypeFields()}
                </div>
              </div>
            )}

            {/* Step 3: Media */}
            {currentStep === 3 && (
              <>
                <div className="media-file">
                  {/* Image Upload */}
                <div className="form-section">
                  <div className="section-header">
                    <h2><i className="fas fa-images"></i> Property Images</h2>
                  </div>
                  <div className="image-upload-section">
                    <label
                      htmlFor="property-images"
                      className="upload-container"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.add('drag-over');
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove('drag-over');
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove('drag-over');
                        const files = Array.from(e.dataTransfer.files);
                        handleImageChange({ target: { files } });
                      }}
                    >
                      <input
                        type="file"
                        id="property-images"
                        name="image"
                        onChange={handleImageChange}
                        multiple
                        accept="image/*"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="upload-content">
                      <div className="upload-icon">
                          <i className="fas fa-video"></i>
                        </div>
                        <h3>Upload Property Images</h3>
                        <p>Drag & drop your images here or click to browse</p>
                      </div>
                    </label>

                    {previewImages.length > 0 && (
                      <div className="preview-grid">
                        {previewImages.map((preview, index) => (
                          <div key={index} className="preview-card">
                            <img src={preview} alt={`Preview ${index + 1}`} />
                            <button
                              type="button"
                              className="remove-btn"
                              onClick={() => handleRemoveImage(index)}
                              title="Remove image"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                            <div className="image-number">Image {index + 1}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Video Upload */}
                <div className="form-section">
                  <div className="section-header">
                    <h2><i className="fas fa-video"></i> Property Video Tour</h2>
                  </div>
                  <div className="video-upload-section">
                    <label
                      htmlFor="property-video"
                      className="upload-container"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.add('drag-over');
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove('drag-over');
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove('drag-over');
                        const files = Array.from(e.dataTransfer.files);
                        if (files.length > 0) {
                          handleVideoChange({ target: { files: [files[0]] } });
                        }
                      }}
                    >
                      <input
                        type="file"
                        id="property-video"
                        name="property_tour"
                        onChange={handleVideoChange}
                        accept="video/*"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="upload-content">
                        <div className="upload-icon">
                          <i className="fas fa-video"></i>
                        </div>
                        <h3>Upload Property Video Tour</h3>
                        <p>Drag & drop your video here or click to browse</p>
                      </div>
                    </label>

                    {videoPreview && (
                      <div className="video-preview">
                        <video controls className="preview-video">
                          <source src={videoPreview} type={formData.property_tour.type} />
                          Your browser does not support the video tag.
                        </video>
                        <button
                          type="button"
                          className="remove-btn"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, property_tour: null }));
                            URL.revokeObjectURL(videoPreview);
                            setVideoPreview(null);
                          }}
                          title="Remove video"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                </div>
              </>
            )}

            {/* Actions */}
            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate('/landlord/properties')}
                className="cancel-btn"
              >
                <i className="fas fa-times"></i>
                Cancel
              </button>

              {currentStep > 1 && (
                <button
                  type="button"
                  className="submit-btn"
                  style={{ background: '#9e9e9e' }}
                  onClick={goToPrevStep}
                >
                  <i className="fas fa-arrow-left"></i>
                  Back
                </button>
              )}

              {currentStep < 3 && (
                <button
                  type="button"
                  className="submit-btn"
                  disabled={isLoading}
                  onClick={goToNextStep}
                >
                  <i className="fas fa-arrow-right"></i>
                  Next
                </button>
              )}

              {currentStep === 3 && (
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isLoading || !formData.property_type}
                >
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Creating Property...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-plus"></i>
                      Create Property
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Update the style element with new video preview styles
const style = document.createElement('style');
style.innerHTML = `
.video-upload-section {
  margin: 20px 0;
}

.video-preview {
  margin-top: 20px;
  position: relative;
  max-width: 600px; /* Reduced from 100% to 600px */
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.preview-video {
  width: 100%;
  max-height: 300px; /* Reduced from 400px to 300px */
  object-fit: contain;
  background: #000;
}

.video-preview .remove-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;
  z-index: 1;
}

.video-preview .remove-btn:hover {
  background: rgba(0, 0, 0, 0.9);
}

/* Add responsive styles */
@media screen and (max-width: 768px) {
  .video-preview {
    max-width: 100%;
  }
  
  .preview-video {
    max-height: 250px;
  }
}

@media screen and (max-width: 480px) {
  .preview-video {
    max-height: 200px;
  }
}
`;
document.head.appendChild(style);

export default AddProperty;
